import * as React from 'react'
import { X } from 'lucide-react'

import {
    AI_TONE_BORDER,
    ICON_BUTTON,
    META_TEXT,
    TOAST_Z_INDEX,
} from './toastStyles'

/** The guide's slot: fixed, centred under the header. Extra props land on the fixed element. */
export function ToastAnchor({ className = '', children, ...anchorProps }) {
    return (
        <div className="project-notes-scope">
            <div
                {...anchorProps}
                className={`tw-fixed ${className}`}
                style={{
                    top: 64,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: TOAST_Z_INDEX,
                }}
            >
                {children}
            </div>
        </div>
    )
}

/** Blurs the page behind a modal card and swallows its clicks. */
function ModalBackdrop() {
    return (
        <div className="project-notes-scope">
            <div
                aria-hidden="true"
                data-testid="toast-modal-backdrop"
                className="tw-fixed tw-inset-0 tw-bg-black/20"
                style={{
                    zIndex: TOAST_Z_INDEX - 1,
                    // Inline: Tailwind's filter utilities compute to none with preflight off.
                    backdropFilter: 'blur(6px)',
                    WebkitBackdropFilter: 'blur(6px)',
                }}
            />
        </div>
    )
}

/**
 * The card every guide-slot toast renders through: the guide's steps, the
 * persona question and the upgrade reminder differ only in their content.
 * The header row is the eyebrow, plus a close button when `onClose` is given.
 * `modal` puts a blurring backdrop behind the card, so only the card is clickable.
 */
export default function ToastCard({
    eyebrow,
    onClose,
    closeLabel = 'Close',
    tone = 'default',
    role = 'status',
    modal = false,
    labelledBy,
    testId,
    anchorProps,
    children,
}) {
    return (
        <>
            {modal ? <ModalBackdrop /> : null}
            <ToastAnchor
                className="tw-w-full tw-max-w-[35rem] tw-px-4"
                {...anchorProps}
            >
                <div
                    role={role}
                    aria-live={role === 'status' ? 'polite' : undefined}
                    aria-modal={modal || undefined}
                    aria-labelledby={labelledBy}
                    data-testid={testId}
                    className={[
                        'tw-pointer-events-auto tw-max-h-[calc(100vh-5rem)] tw-overflow-y-auto tw-rounded-2xl tw-border tw-border-solid tw-bg-background tw-p-5 tw-text-foreground tw-shadow-xl',
                        tone === 'ai' ? AI_TONE_BORDER : 'tw-border-border',
                    ].join(' ')}
                >
                    <div className="tw-mb-3 tw-flex tw-min-h-7 tw-items-center tw-justify-between tw-gap-3">
                        <span
                            className={`${META_TEXT} tw-text-muted-foreground`}
                        >
                            {eyebrow}
                        </span>
                        {onClose ? (
                            <button
                                type="button"
                                onClick={onClose}
                                aria-label={closeLabel}
                                className={ICON_BUTTON}
                            >
                                <X
                                    className="tw-h-3.5 tw-w-3.5"
                                    aria-hidden="true"
                                />
                            </button>
                        ) : null}
                    </div>
                    {children}
                </div>
            </ToastAnchor>
        </>
    )
}
